import React from 'react';
import { ArrowLeft, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-950 text-gray-200">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 bg-gray-950/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-yellow-500" />
            <h1 className="text-lg font-semibold text-white">Terms of Service</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        <div className="text-sm text-gray-500">
          Last updated: March 25, 2026
        </div>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">1. Acceptance of Terms</h2>
          <p>
            By accessing or using ProsperMind, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using this service.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">2. Description of Service</h2>
          <p>
            ProsperMind is a personal financial wellness platform that helps users track expenses, manage finances, and gain insights into their financial health. The service includes:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Expense tracking and categorization</li>
            <li>Financial data visualization</li>
            <li>AI-generated financial insights</li>
            <li>Goal setting and progress tracking</li>
            <li>Educational content about personal finance</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">3. User Accounts</h2>
          <p>To use ProsperMind, you must:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Be at least 18 years old</li>
            <li>Provide accurate and complete registration information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Notify us immediately of any unauthorized access</li>
            <li>Be responsible for all activities under your account</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">4. Financial Information Disclaimer</h2>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <p className="text-yellow-400 font-medium mb-2">Important Notice:</p>
            <p>
              ProsperMind provides general financial information and AI-generated insights for educational and informational purposes only. This service does NOT provide:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2 text-gray-300">
              <li>Financial advice</li>
              <li>Investment advice</li>
              <li>Tax advice</li>
              <li>Legal advice</li>
            </ul>
            <p className="mt-2">
              Always consult with qualified professionals before making financial decisions.
            </p>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">5. AI-Generated Content</h2>
          <p>
            Our service uses artificial intelligence to generate insights and recommendations. You acknowledge that:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>AI-generated content may not always be accurate</li>
            <li>Insights are based on the data you provide</li>
            <li>You should verify important information independently</li>
            <li>We are not liable for decisions made based on AI insights</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">6. User Responsibilities</h2>
          <p>You agree to:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Provide accurate financial information</li>
            <li>Use the service for lawful purposes only</li>
            <li>Not attempt to breach security measures</li>
            <li>Not share your account with others</li>
            <li>Not use automated systems to access the service</li>
            <li>Respect intellectual property rights</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">7. Subscription and Payments</h2>
          <p>
            ProsperMind offers free and premium subscription plans. For premium features:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Payments are processed securely through Stripe</li>
            <li>Subscriptions auto-renew unless cancelled</li>
            <li>Refunds are handled according to our refund policy</li>
            <li>Prices may change with notice</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">8. Intellectual Property</h2>
          <p>
            All content, features, and functionality of ProsperMind are owned by us and protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our permission.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">9. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, ProsperMind shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or other intangible losses.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">10. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless ProsperMind and its affiliates from any claims, damages, or expenses arising from your use of the service or violation of these terms.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">11. Termination</h2>
          <p>
            We may terminate or suspend your account at any time for violations of these terms. You may also delete your account at any time through your account settings.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">12. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">13. Changes to Terms</h2>
          <p>
            We reserve the right to modify these terms at any time. We will provide notice of significant changes. Continued use of the service after changes constitutes acceptance of the new terms.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">14. Contact</h2>
          <p>
            For questions about these Terms of Service, please contact us at:
          </p>
          <p className="text-yellow-500">legal@prospermind.com</p>
        </section>

        {/* Footer spacing */}
        <div className="h-16" />
      </div>
    </div>
  );
};

export default TermsOfService;
